// Chemin: C:\gestion-copro\src\pages\vigie\CourrierFormVigiePage.tsx
// Formulaire courrier VIGIE — Version simplifiée sans interlocuteurs, affaires, votes
// Session 100 : Placeholder
// Session 102 : Version fonctionnelle — création/édition brouillon, destinataire manuel
// Session 103 : Intégration génération IA depuis cockpit SENTINELLE (promptUtilisateur)
// Session 114 : Fix extraction robuste réponse SENTINELLE (objet/contenu dans data.message ou data.agent)
// Session 115 : Suppression prompt local — le systemPrompt serveur gère tout.
//               Ajout mode "email" dans les options. Défaut postal au lieu de LRAR.
// Session 116 : Nettoyage console.log debug.
// Session 117 : REFONTE — Suppression génération IA autonome. Le formulaire ne fait plus
//               que recevoir des données pré-remplies depuis le chat SENTINELLE (sas unique).
//               Plus de promptUtilisateur, plus d'appel askAssistant direct.
// Session 121 : Validation adaptative — adresse postale obligatoire uniquement si mode ≠ email.
//               Email obligatoire si mode = email. Bouton retour → "Mes correspondances".
// Session 122 : Liaison contacts — recherche automatique dans les interlocuteurs VIGIE
//               pour pré-remplir email/adresse. Bandeaux contact manquant / email manquant.
// Session 126 : Flag modifieParUtilisateur à l'édition pour conditionner SCUD
// Session 128 : Rattachement automatique — accepte signalementId + affaireId depuis prefill SENTINELLE
// Session 129 : Traçabilité chronologie — ajout événement dans le dossier lors du rattachement auto
// Session 160 : Champ CC (copie carbone) — sélection parmi les contacts enregistrés
// Session 173 : Dropdown type destinataire dynamique depuis TYPE_INTERLOCUTEUR_LABELS — suppression hardcoding
// Session 175 : Suppression filet de sécurité type courrier (cause racine corrigée dans systemPrompt)
// Session 234 : Création document GED miroir après createCourrier() — lien documentGedId
// Session 235 : Rattachement automatique au dossier via rattacherCourrier() — liaison réelle
//               pour synchroniser estArchive entre GED et Mes correspondances
// Session 271 : Fix P0 — catégorie GED miroir corrigée ('administratif' au lieu de codes invalides SYN/PRO/TEC/AUT)
//               Navigation post-save recâblée vers /vigie/documents (Mes documents) — /vigie/courriers route morte
// Workflow VIGIE : brouillon → finalisé → envoyé (pas de validation CS)

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCopropriete } from '../../context/CoproprieteContext';
import {
  createCourrier,
  getCourrierById,
  updateCourrier
} from '../../services/courrier.service';
import { rattacherCourrier } from '../../services/affaire.service';
import { doc, updateDoc, addDoc, collection, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';
import {
  getInterlocuteurs
} from '../../services/interlocuteur.service';
// Session 134 : import addEvenementToAffaire retiré — chronologie ajoutée à l'envoi, pas au brouillon
import { Interlocuteur, TYPE_INTERLOCUTEUR_LABELS } from '../../types/interlocuteur.types';
import {
  TypeCourrier,
  ModeEnvoi,
  CourrierCreateData,
  CourrierUpdateData,
  DestinataireCourrier,
  TYPE_COURRIER_LABELS,
  MODE_ENVOI_LABELS
} from '../../types/courrier.types';
import {
  FileText,
  ArrowLeft,
  Save,
  Loader2,
  AlertCircle,
  Sparkles,
  Shield,
  UserPlus,
  X,
  Plus,
  Users,
  CheckCircle
} from 'lucide-react';

// ============================================
// TYPES
// ============================================

interface FormData {
  objet: string;
  contenu: string;
  type: TypeCourrier;
  modeEnvoi: ModeEnvoi;
  destinataireNom: string;
  destinataireEmail: string;
  destinataireAdresse: string;
  destinataireCategorie: DestinataireCourrier['type'];
}

// Session 117 : State passé depuis le chat SENTINELLE inline (SentinelleVigiePage)
// Plus de promptUtilisateur — SENTINELLE a déjà dialogué et qualifié.
// Le formulaire reçoit les données pré-remplies prêtes à éditer.
interface SentinelleNavigationState {
  fromSentinelle?: boolean;
  prefill?: {
    objet?: string;
    contenu?: string;
    type?: string;
    destinataire?: string;
    destinataireType?: string;
    modeEnvoi?: string;
    // Session 128 : Rattachement automatique
    signalementId?: string;
    affaireId?: string;
    // Session 160 : CC suggérés par SENTINELLE
    destinatairesCC?: Array<{ nom: string; type?: string }>;
    // Session 216 : ID du courrier d'origine (pour calcul niveau relance)
    courrierId_lie?: string;
    // Session 292 : Email du signataire du document source (priorité sur contact principal)
    destinataireEmail?: string;
  };
}

const initialFormData: FormData = {
  objet: '',
  contenu: '',
  type: 'demande_information',
  modeEnvoi: 'postal',
  destinataireNom: '',
  destinataireEmail: '',
  destinataireAdresse: '',
  destinataireCategorie: 'syndic',
};

// Session 115 : Modes d'envoi avec email
const MODES_ENVOI: { value: ModeEnvoi; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'postal', label: 'Courrier simple' },
  { value: 'lrar', label: 'Lettre recommandée AR' },
  { value: 'recommande_ar', label: 'Recommandé avec AR' },
];

const TYPES_COURRIER: { value: TypeCourrier; label: string }[] = Object.entries(TYPE_COURRIER_LABELS).map(
  ([value, label]) => ({ value: value as TypeCourrier, label })
);

// Session 173 : Génération dynamique depuis TYPE_INTERLOCUTEUR_LABELS — zéro hardcoding
const CATEGORIES_DESTINATAIRE: { value: DestinataireCourrier['type']; label: string }[] = Object.entries(TYPE_INTERLOCUTEUR_LABELS).map(
  ([value, label]) => ({ value: value as DestinataireCourrier['type'], label })
);

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const CourrierFormVigiePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: courrierId } = useParams<{ id: string }>();
  const { userProfile, firebaseUser } = useAuth();
  const { coproprieteId } = useCopropriete();

  const isEdition = !!courrierId;

  // Session 117 : State de navigation depuis le chat SENTINELLE inline
  const navState = location.state as SentinelleNavigationState | null;
  const sentinellePrefill = navState?.fromSentinelle ? navState.prefill : null;

  // Session 222 : Capturer le prefill dans un ref stable AU MONTAGE.
  // window.history.replaceState() efface location.state immédiatement — si loading
  // change et relance le useEffect, sentinellePrefill vaut null → formulaire vide.
  // Le ref est initialisé une seule fois et survit aux re-renders.
  const sentinellePrefillRef = useRef(sentinellePrefill);

  // États
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [loading, setLoading] = useState(isEdition);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Guard anti-double-soumission — useRef survit aux re-renders contrairement à useState
  const submittingRef = useRef(false);

  // Session 117 : Indicateur visuel "pré-rempli par SENTINELLE"
  const [genereParSentinelle, setGenereParSentinelle] = useState(false);

  // Session 122 : Liaison contacts — résultat de la recherche automatique
  const [contactTrouve, setContactTrouve] = useState<Interlocuteur | null>(null);
  const [contactManquant, setContactManquant] = useState(false); // Destinataire pas dans les contacts
  const [emailManquant, setEmailManquant] = useState(false);     // Contact trouvé mais sans email
  const [reformulationsAppliquees, setReformulationsAppliquees] = useState(0); // Session 301 : reformulations SCUD injectées

  // Session 160 : Champ CC — contacts disponibles et sélectionnés
  const [tousLesContacts, setTousLesContacts] = useState<Interlocuteur[]>([]);
  const [destinatairesCC, setDestinatairesCC] = useState<DestinataireCourrier[]>([]);
  const [showCCSelector, setShowCCSelector] = useState(false);

  // Nom d'affichage utilisateur
  const getUserDisplayName = (): string => {
    if (userProfile?.prenom && userProfile?.nom) {
      return `${userProfile.prenom} ${userProfile.nom}`.trim();
    }
    return userProfile?.email || 'Utilisateur';
  };

  // Chargement en édition
  useEffect(() => {
    const chargerCourrier = async () => {
      if (!courrierId) return;

      setLoading(true);
      try {
        const courrier = await getCourrierById(courrierId);
        if (!courrier) {
          setError('Courrier non trouvé');
          return;
        }
        if (courrier.statut !== 'brouillon') {
          setError('Ce courrier ne peut plus être modifié');
          return;
        }

        // Session 301 : Appliquer les reformulations SCUD si analyse disponible
        let contenuFinal = courrier.contenu;
        let nbReformulations = 0;
        if (courrier.analyseSentinelle?.alertes?.length) {
          for (const alerte of courrier.analyseSentinelle.alertes) {
            if (alerte.extrait && alerte.suggestion && contenuFinal.includes(alerte.extrait)) {
              contenuFinal = contenuFinal.replace(alerte.extrait, alerte.suggestion);
              nbReformulations++;
            }
          }
        }
        if (nbReformulations > 0) setReformulationsAppliquees(nbReformulations);

        setFormData({
          objet: courrier.objet,
          contenu: contenuFinal,
          type: courrier.type,
          modeEnvoi: courrier.modeEnvoi,
          destinataireNom: courrier.destinataire?.nom || '',
          destinataireEmail: courrier.destinataire?.email || '',
          destinataireAdresse: courrier.destinataire?.adresse || '',
          destinataireCategorie: courrier.destinataire?.type || 'autre',
        });
        // Session 160 : Charger les CC existants
        if (courrier.destinatairesCC && courrier.destinatairesCC.length > 0) {
          setDestinatairesCC(courrier.destinatairesCC);
        }
      } catch (err) {
        console.error('Erreur chargement courrier:', err);
        setError('Impossible de charger le courrier');
      } finally {
        setLoading(false);
      }
    };

    chargerCourrier();
  }, [courrierId]);

  // Session 117 : Prefill depuis le chat SENTINELLE (données déjà qualifiées)
  // Session 122 : Enrichissement automatique depuis les contacts enregistrés
  // Session 160 : Chargement de tous les contacts pour le sélecteur CC
  useEffect(() => {
    const chargerContacts = async () => {
      if (!coproprieteId) return;
      try {
        const contacts = await getInterlocuteurs(coproprieteId, true);
        setTousLesContacts(contacts);
      } catch (err) {
        console.error('Erreur chargement contacts CC:', err);
      }
    };
    chargerContacts();
  }, [coproprieteId]);

  useEffect(() => {
    if (!sentinellePrefillRef.current || loading || isEdition) return;

    const prefillData: FormData = {
      ...initialFormData,
      objet: sentinellePrefillRef.current.objet || initialFormData.objet,
      contenu: sentinellePrefillRef.current.contenu || '',
      type: (sentinellePrefillRef.current.type as TypeCourrier) || initialFormData.type,
      modeEnvoi: (sentinellePrefillRef.current.modeEnvoi as ModeEnvoi) || initialFormData.modeEnvoi,
      destinataireNom: sentinellePrefillRef.current.destinataire || initialFormData.destinataireNom,
      destinataireCategorie: (sentinellePrefillRef.current.destinataireType as DestinataireCourrier['type']) || initialFormData.destinataireCategorie,
    };

    // Recherche automatique dans les contacts
    const rechercherContact = async () => {
      if (!coproprieteId || !sentinellePrefillRef.current!.destinataire) {
        setFormData(prefillData);
        setGenereParSentinelle(true);
        return;
      }

      try {
        const interlocuteurs = await getInterlocuteurs(coproprieteId, true);
        const nomRecherche = sentinellePrefillRef.current!.destinataire!.toLowerCase();
        const typeRecherche = sentinellePrefillRef.current!.destinataireType?.toLowerCase();

        // Recherche par nom (correspondance partielle) puis par type
        let trouve = interlocuteurs.find(i => 
          i.raisonSociale.toLowerCase().includes(nomRecherche) ||
          nomRecherche.includes(i.raisonSociale.toLowerCase())
        );

        // Fallback : recherche par type si pas trouvé par nom
        if (!trouve && typeRecherche) {
          trouve = interlocuteurs.find(i => i.type === typeRecherche);
        }

        if (trouve) {
          setContactTrouve(trouve);
          const cp = trouve.contacts?.find(c => c.principal) || trouve.contacts?.[0];

          // Session 292 : Priorité à l'email du signataire injecté par SENTINELLE (destinataireEmail)
          // sur l'email du contact principal Firestore — SENTINELLE a identifié la bonne personne
          if (sentinellePrefillRef.current!.destinataireEmail) {
            prefillData.destinataireEmail = sentinellePrefillRef.current!.destinataireEmail;
          } else if (cp?.email) {
            prefillData.destinataireEmail = cp.email;
          } else {
            setEmailManquant(true);
          }

          if (trouve.adresse?.rue) {
            const adresseParts = [
              trouve.adresse.rue,
              [trouve.adresse.codePostal, trouve.adresse.ville].filter(Boolean).join(' ')
            ].filter(Boolean);
            prefillData.destinataireAdresse = adresseParts.join('\n');
          }

          // Utiliser la raison sociale exacte du contact
          prefillData.destinataireNom = trouve.raisonSociale;
        } else {
          setContactManquant(true);
        }
      } catch (err) {
        console.error('Erreur recherche contacts:', err);
        // Pas bloquant — on continue avec le prefill brut
      }

      // Session 160 : Matcher les CC suggérés par SENTINELLE avec les contacts existants
      if (Array.isArray(sentinellePrefillRef.current!.destinatairesCC) && sentinellePrefillRef.current!.destinatairesCC!.length > 0) {
        try {
          const interlocuteurs = tousLesContacts.length > 0 
            ? tousLesContacts 
            : (coproprieteId ? await getInterlocuteurs(coproprieteId, true) : []);
          
          const ccResolus: DestinataireCourrier[] = [];
          for (const ccSuggere of sentinellePrefillRef.current!.destinatairesCC!) {
            const nomCC = (ccSuggere.nom || '').toLowerCase();
            const typeCC = (ccSuggere.type || '').toLowerCase();
            
            // Chercher par nom (correspondance partielle) puis par type
            let contactCC = interlocuteurs.find(i => 
              i.raisonSociale.toLowerCase().includes(nomCC) ||
              nomCC.includes(i.raisonSociale.toLowerCase())
            );
            if (!contactCC && typeCC) {
              contactCC = interlocuteurs.find(i => i.type === typeCC);
            }
            
            if (contactCC) {
              const cpCC = contactCC.contacts?.find(c => c.principal) || contactCC.contacts?.[0];
              const ccEntry: DestinataireCourrier = {
                type: contactCC.type as DestinataireCourrier['type'] || 'autre',
                interlocuteurId: contactCC.id,
                nom: contactCC.raisonSociale,
              };
              if (cpCC?.email) ccEntry.email = cpCC.email;
              if (contactCC.adresse?.rue) {
                const adresseParts = [
                  contactCC.adresse.rue,
                  [contactCC.adresse.codePostal, contactCC.adresse.ville].filter(Boolean).join(' ')
                ].filter(Boolean);
                ccEntry.adresse = adresseParts.join(', ');
              }
              ccResolus.push(ccEntry);
            }
          }
          if (ccResolus.length > 0) {
            setDestinatairesCC(ccResolus);
          }
        } catch (err) {
          console.error('Erreur matching CC:', err);
        }
      }

      setFormData(prefillData);
      setGenereParSentinelle(true);
    };

    rechercherContact();
    // Nettoyer le state pour éviter re-application au remount
    window.history.replaceState({}, document.title);
  }, [loading, isEdition, coproprieteId]);

  // ============================================
  // SESSION 160 : GESTION CC
  // ============================================

  /**
   * Contacts disponibles pour le CC (exclut le destinataire principal et les CC déjà ajoutés)
   */
  const contactsDisponiblesCC = tousLesContacts.filter(contact => {
    // Exclure le destinataire principal (par nom)
    if (contact.raisonSociale.toLowerCase() === formData.destinataireNom.toLowerCase()) return false;
    // Exclure les CC déjà sélectionnés (par interlocuteurId ou par nom)
    if (destinatairesCC.some(cc => 
      (cc.interlocuteurId && cc.interlocuteurId === contact.id) ||
      cc.nom.toLowerCase() === contact.raisonSociale.toLowerCase()
    )) return false;
    return true;
  });

  /**
   * Ajouter un contact existant en CC
   */
  const ajouterContactCC = (contact: Interlocuteur) => {
    const cp = contact.contacts?.find(c => c.principal) || contact.contacts?.[0];
    const ccEntry: DestinataireCourrier = {
      type: contact.type as DestinataireCourrier['type'] || 'autre',
      interlocuteurId: contact.id,
      nom: contact.raisonSociale,
    };
    if (cp?.email) ccEntry.email = cp.email;
    if (contact.adresse?.rue) {
      const adresseParts = [
        contact.adresse.rue,
        [contact.adresse.codePostal, contact.adresse.ville].filter(Boolean).join(' ')
      ].filter(Boolean);
      ccEntry.adresse = adresseParts.join(', ');
    }
    setDestinatairesCC(prev => [...prev, ccEntry]);
    setShowCCSelector(false);
  };

  /**
   * Supprimer un CC
   */
  const supprimerCC = (index: number) => {
    setDestinatairesCC(prev => prev.filter((_, i) => i !== index));
  };

  // Mise à jour formulaire
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Validation — Session 121 : adaptative selon le mode d'envoi
  const validateForm = (): string | null => {
    if (!formData.objet.trim()) return "L'objet est obligatoire";
    if (!formData.contenu.trim()) return 'Le contenu est obligatoire';
    if (!formData.destinataireNom.trim()) return 'Le nom du destinataire est obligatoire';
    // Email obligatoire si mode = email
    if (formData.modeEnvoi === 'email' && !formData.destinataireEmail.trim()) {
      return "L'adresse email du destinataire est obligatoire pour un envoi par email";
    }
    // Adresse postale obligatoire uniquement si mode ≠ email
    if (formData.modeEnvoi !== 'email' && !formData.destinataireAdresse.trim()) {
      return "L'adresse postale du destinataire est obligatoire pour un envoi postal";
    }
    return null;
  };

  // Soumission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Guard anti-double-soumission — bloque immédiatement avant tout await
    if (submittingRef.current) return;
    submittingRef.current = true;

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      submittingRef.current = false;
      return;
    }

    if (!coproprieteId || !firebaseUser || !userProfile) {
      setError('Session invalide');
      submittingRef.current = false;
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const destinataire: DestinataireCourrier = {
        type: formData.destinataireCategorie,
        nom: formData.destinataireNom,
      };
      if (formData.destinataireEmail.trim()) {
        destinataire.email = formData.destinataireEmail;
      }
      if (formData.destinataireAdresse.trim()) {
        destinataire.adresse = formData.destinataireAdresse;
      }

      if (isEdition && courrierId) {
        const updateData: CourrierUpdateData = {
          objet: formData.objet,
          contenu: formData.contenu,
          type: formData.type,
          modeEnvoi: formData.modeEnvoi,
          destinataire,
          destinatairesCC: destinatairesCC.length > 0 ? destinatairesCC : undefined, // Session 160
          modifieParUtilisateur: true, // Session 126 : flag pour conditionner l'analyse SCUD
        };
        await updateCourrier(courrierId, updateData);
      } else {
        const createData: CourrierCreateData = {
          coproprieteId,
          objet: formData.objet,
          contenu: formData.contenu,
          type: formData.type,
          modeEnvoi: formData.modeEnvoi,
          destinataire,
          destinatairesCC: destinatairesCC.length > 0 ? destinatairesCC : undefined, // Session 160
          redacteurId: firebaseUser.uid,
          redacteurNom: getUserDisplayName(),
          // Session 128 : Rattachement automatique depuis contexte SENTINELLE
          ...(sentinellePrefillRef.current?.signalementId && { signalementIds: [sentinellePrefillRef.current.signalementId] }),
          ...(sentinellePrefillRef.current?.affaireId && { affaireId: sentinellePrefillRef.current.affaireId }),
          // Session 216 : Rattachement au courrier d'origine pour calcul niveau relance
          ...(sentinellePrefillRef.current?.courrierId_lie && { courrierReponseA: sentinellePrefillRef.current.courrierId_lie }),
        };
        // VIGIE : votesRequis = 0 (pas de validation CS)
        const newCourrierId = await createCourrier(createData, 0);

        // Session 234 : Créer le document GED miroir — archivé dans Mes documents
        // Session 271 : Catégorie corrigée → 'administratif' (valide) au lieu des codes courts invalides SYN/PRO/TEC/AUT
        // Session 271 : Plus d'upload Storage (.txt refusé par les règles) — écriture Firestore directe
        //               Le contenu est un texte structuré, pas un fichier binaire
        try {
          // Session 271 : Catégorie GED déduite du destinataire — syndic → 'syndic', promoteur → 'promoteur', etc.
          const categorieGED = (() => {
            switch (formData.destinataireCategorie) {
              case 'syndic':        return 'syndic';
              case 'promoteur':     return 'promoteur';
              case 'prestataire':
              case 'constructeur':  return 'technique';
              default:              return 'administratif';
            }
          })();

          const gedData = {
            coproprieteId,
            titre: formData.objet,
            // type GED = canal (email / courrier postal) — modeEnvoi = canal précis
            type: formData.modeEnvoi === 'email' ? 'email' : 'courrier',
            modeEnvoi: formData.modeEnvoi, // Session 271 : canal précis (postal, lrar, recommande_ar, email)
            categorie: categorieGED,
            description: formData.contenu.substring(0, 200),
            contenuTexte: formData.contenu,
            fichierUrl: '',
            fichierNom: `courrier_${newCourrierId}.txt`,
            fichierTaille: 0,
            fichierType: 'text/plain',
            interlocuteurNom: formData.destinataireNom || null,
            interlocuteurId: null,
            dateDocument: Timestamp.now(),
            visibilite: 'cs_only',
            affaireId: sentinellePrefillRef.current?.affaireId || null,
            uploadeParId: firebaseUser.uid,
            uploadeParNom: getUserDisplayName(),
            source: 'sentinelle_vigie',
            courrierId: newCourrierId, // Session 272 : lien direct — evite query filtree bloquee par regles Firestore
            statutCourrier: 'brouillon', // Session 272 : statut initial
            sensCorrespondance: 'sortant',
            // Session 276 : resumeIA = résumé lisible par SENTINELLE
            // Sans ce champ, SENTINELLE marquait le courrier [CONTENU NON ANALYSÉ]
            // et ne pouvait pas raisonner sur son propre courrier généré
            resumeIA: `Courrier ${formData.type} envoyé à ${formData.destinataireNom || 'destinataire'} — ${formData.contenu.substring(0, 400)}`,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          };
          const gedRef = await addDoc(collection(db, 'documents'), gedData);
          const documentGedId = gedRef.id;
          // Poser documentGedId sur le courrier pour lier les deux
          await updateDoc(doc(db, 'courriers', newCourrierId), { documentGedId });
        } catch (gedErr) {
          // Non bloquant — le courrier existe, le lien GED est optionnel
          console.warn('[CourrierFormVigiePage] Erreur création document GED miroir:', gedErr);
        }
        // Session 235 : Rattachement automatique au dossier dès la création du brouillon
        // rattacherCourrier() écrit affaireId sur le courrier ET trace dans la chronologie du dossier
        // Un brouillon rattaché = le dossier voit le courrier immédiatement, même non encore envoyé
        if (sentinellePrefillRef.current?.affaireId) {
          try {
            const userNom = getUserDisplayName();
            await rattacherCourrier(
              sentinellePrefillRef.current.affaireId,
              newCourrierId,
              formData.objet,
              firebaseUser.uid,
              userNom
            );
          } catch (rattachErr) {
            // Non bloquant — le courrier existe, le lien dossier est best-effort
            console.warn('[CourrierFormVigiePage] Erreur rattachement dossier:', rattachErr);
          }
        }
      }

      // Session 286 : En édition → retour sur le courrier avec déclenchement SCUD automatique
      //               En création → retour sur Mes documents (comportement inchangé)
      // Session 301 : Si des reformulations SCUD ont déjà été appliquées, ne pas relancer l'analyse
      //               (l'analyse a eu lieu avant la modification — relancer reviendrait à analyser
      //               des reformulations que SENTINELLE elle-même a produites)
      if (isEdition && courrierId) {
        const relancerSCUD = reformulationsAppliquees === 0;
        navigate(`/vigie/courriers/${courrierId}`, { state: { lancerSCUD: relancerSCUD } });
      } else {
        navigate('/vigie/documents');
      }
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      setError('Impossible de sauvegarder le courrier');
    } finally {
      setSaving(false);
      submittingRef.current = false;
    }
  };

  // --- RENDU ---

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">

      {/* En-tête */}
      <div>
        <button
          onClick={() => navigate('/vigie/documents')}
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-3"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour à Mes documents
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdition ? 'Modifier le courrier' : 'Nouveau courrier'}
        </h1>
        <p className="text-gray-600 mt-1">
          {isEdition
            ? 'Modifiez le brouillon avant de le finaliser.'
            : 'Vérifiez le contenu rédigé par SENTINELLE, ajustez si nécessaire, puis enregistrez.'
          }
        </p>
      </div>

      {/* Session 301 : Bandeau reformulations SCUD appliquées */}
      {reformulationsAppliquees > 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
          <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
            <CheckCircle className="w-4 h-4 text-amber-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-amber-900">
              {reformulationsAppliquees} reformulation{reformulationsAppliquees > 1 ? 's' : ''} SENTINELLE appliquée{reformulationsAppliquees > 1 ? 's' : ''}
            </p>
            <p className="text-xs text-amber-700">
              Les passages signalés ont été corrigés automatiquement. Vérifiez le texte avant d'enregistrer.
            </p>
          </div>
        </div>
      )}

      {/* Bandeau SENTINELLE si pré-rempli par IA */}
      {genereParSentinelle && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-blue-900">Rédigé par SENTINELLE (méthode SCUD)</p>
            <p className="text-xs text-blue-700">
              Vérifiez le contenu, ajustez les informations du destinataire, puis enregistrez.
            </p>
          </div>
        </div>
      )}

      {/* Session 122 : Bandeau contact manquant */}
      {contactManquant && genereParSentinelle && (
        <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg flex items-center gap-3">
          <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
            <UserPlus className="w-4 h-4 text-orange-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-orange-900">Destinataire inconnu</p>
            <p className="text-xs text-orange-700">
              Ce destinataire n'est pas dans tes contacts. Ajoute-le pour que SENTINELLE pré-remplisse ses coordonnées automatiquement la prochaine fois.
            </p>
          </div>
          <Link
            to="/vigie/contacts"
            className="px-3 py-1.5 text-xs font-medium text-orange-700 bg-orange-100 hover:bg-orange-200 rounded-lg transition-colors whitespace-nowrap"
          >
            Ajouter un contact
          </Link>
        </div>
      )}

      {/* Session 122 : Bandeau email manquant sur un contact existant */}
      {emailManquant && !contactManquant && genereParSentinelle && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
          <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-4 h-4 text-amber-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-900">Email non renseigné pour ce contact</p>
            <p className="text-xs text-amber-700">
              {contactTrouve?.raisonSociale} est dans tes contacts mais sans adresse email. Complète sa fiche pour faciliter tes prochains envois.
            </p>
          </div>
          <Link
            to="/vigie/contacts"
            className="px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors whitespace-nowrap"
          >
            Compléter le contact
          </Link>
        </div>
      )}

      {/* Erreur */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Formulaire */}
      <form onSubmit={handleSubmit} className="space-y-6">

        {/* Objet */}
        <div className="bg-white rounded-xl shadow-sm p-5 space-y-4">
          <h2 className="font-semibold text-gray-900">Courrier</h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Objet *</label>
            <input
              type="text"
              name="objet"
              required
              value={formData.objet}
              onChange={handleChange}
              placeholder="Ex: Mise en demeure - Malfaçons parties communes"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              >
                {TYPES_COURRIER.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mode d'envoi</label>
              <select
                name="modeEnvoi"
                value={formData.modeEnvoi}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              >
                {MODES_ENVOI.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contenu *</label>
            <textarea
              name="contenu"
              required
              value={formData.contenu}
              onChange={handleChange}
              rows={12}
              placeholder="Le contenu sera rédigé par SENTINELLE — demande-lui de générer un courrier depuis le chat."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 font-mono text-sm"
            />
          </div>
        </div>

        {/* Destinataire */}
        <div className="bg-white rounded-xl shadow-sm p-5 space-y-4">
          <h2 className="font-semibold text-gray-900">Destinataire</h2>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
              <input
                type="text"
                name="destinataireNom"
                required
                value={formData.destinataireNom}
                onChange={handleChange}
                placeholder="Ex: Square Habitat Nord de France"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                name="destinataireCategorie"
                value={formData.destinataireCategorie}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              >
                {CATEGORIES_DESTINATAIRE.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Adresse postale {formData.modeEnvoi !== 'email' ? '*' : '(optionnel)'}
            </label>
            <textarea
              name="destinataireAdresse"
              required={formData.modeEnvoi !== 'email'}
              value={formData.destinataireAdresse}
              onChange={handleChange}
              placeholder="Adresse complète du destinataire"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email {formData.modeEnvoi === 'email' ? '*' : '(optionnel)'}
            </label>
            <input
              type="email"
              name="destinataireEmail"
              required={formData.modeEnvoi === 'email'}
              value={formData.destinataireEmail}
              onChange={handleChange}
              placeholder="contact@syndic.fr"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
          </div>

          {/* Session 160 : Champ CC (Copie Carbone) */}
          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                En copie (CC)
              </label>
              {contactsDisponiblesCC.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowCCSelector(!showCCSelector)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 hover:text-amber-700 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Ajouter un CC
                </button>
              )}
            </div>

            {/* CC déjà ajoutés */}
            {destinatairesCC.length > 0 && (
              <div className="space-y-2 mb-3">
                {destinatairesCC.map((cc, index) => (
                  <div
                    key={`cc-${index}`}
                    className="flex items-center gap-3 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm"
                  >
                    <Users className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-gray-700">{cc.nom}</span>
                      {cc.email && (
                        <span className="text-gray-400 ml-2">· {cc.email}</span>
                      )}
                      {!cc.email && (
                        <span className="text-orange-400 ml-2 text-xs">· pas d'email</span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => supprimerCC(index)}
                      className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                      title="Retirer du CC"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Sélecteur CC parmi les contacts */}
            {showCCSelector && contactsDisponiblesCC.length > 0 && (
              <div className="border border-amber-200 rounded-lg bg-amber-50 p-3 space-y-1">
                <p className="text-xs text-amber-700 font-medium mb-2">Sélectionne un contact à mettre en copie :</p>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {contactsDisponiblesCC.map(contact => {
                    const cp = contact.contacts?.find(c => c.principal) || contact.contacts?.[0];
                    return (
                      <button
                        key={contact.id}
                        type="button"
                        onClick={() => ajouterContactCC(contact)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-amber-100 transition-colors text-sm flex items-center gap-2"
                      >
                        <Users className="w-4 h-4 text-amber-500 flex-shrink-0" />
                        <span className="font-medium text-gray-800">{contact.raisonSociale}</span>
                        {cp?.email && (
                          <span className="text-gray-400 text-xs truncate">· {cp.email}</span>
                        )}
                        {!cp?.email && (
                          <span className="text-orange-400 text-xs">· pas d'email</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={() => setShowCCSelector(false)}
                  className="text-xs text-gray-500 hover:text-gray-700 mt-1"
                >
                  Fermer
                </button>
              </div>
            )}

            {/* Message si aucun contact disponible pour CC */}
            {destinatairesCC.length === 0 && contactsDisponiblesCC.length === 0 && tousLesContacts.length > 0 && (
              <p className="text-xs text-gray-400 italic">Tous tes contacts sont déjà destinataire principal ou en copie.</p>
            )}
            {tousLesContacts.length === 0 && (
              <p className="text-xs text-gray-400 italic">
                Aucun contact enregistré.{' '}
                <Link to="/vigie/contacts" className="text-amber-600 hover:underline">Ajouter des contacts</Link>{' '}
                pour pouvoir les mettre en copie.
              </p>
            )}
          </div>
        </div>

        {/* Boutons */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/vigie/documents')}
            className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-5 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {isEdition ? 'Enregistrer les modifications' : 'Enregistrer le brouillon'}
              </>
            )}
          </button>
        </div>

      </form>
    </div>
  );
};

export default CourrierFormVigiePage;
